/**
 * useContactEditForm — edit-mode state machine for the contact-verification
 * takeover (US4).
 *
 * Holds the editable field values, per-field validation, postal→city autofill,
 * email-domain autocomplete, and the submit pipeline for the inline-edit card
 * the prompt renders (mirroring the proto's `UpdateContactsModal`). Extracted
 * from `ContactVerificationForm` so the form/row components stay presentational
 * and the prompt stays under the 400-line ceiling.
 *
 * Behaviour preserved verbatim from the prior `ContactVerificationForm`:
 *   - `validateField` rules (postalCode `^\d{5}$`, email local-part @ dotted
 *     domain, others non-empty), validated for ALL fields on submit.
 *   - on a failed submit, focus moves to the first invalid field (its
 *     `aria-describedby` error is then announced).
 *   - `updateContactDetails` is wrapped in try/catch for
 *     `ContactDetailsValidationError` (defense in depth — the adapter validates
 *     too) and surfaced form-level via `submitError`.
 *   - on success: `recordVerification('updated')` (rejection swallowed) then
 *     `onDone()`.
 *
 * New proto behaviour:
 *   - postal autofill: when the postal field reaches 5 digits, `lookupCity` is
 *     called; a non-null city overwrites the city field, a null leaves the typed
 *     value (matches the proto + the optional, crash-tolerant port contract).
 *   - email autocomplete: as the local part is typed, static local-domain
 *     suggestions (`gmail.com`, `outlook.com`, `hotmail.com`) are offered with no
 *     network (exactly as the proto does); selecting one fills the email field.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Tasks: T040, T041
 */

import { useId, useRef, useState } from 'react';
import type { MessageKeys } from '../i18n/messages/index.js';
import type { ContactDetailsPatch } from '../member-profile/MemberProfilePort.js';
import { useIdempotencyKey } from '../member-profile/useIdempotencyKey.js';
import { useMemberProfile, useMemberProfileWriter } from '../member-profile/useMemberProfile.js';
import { usePostalCodeLookup } from './usePostalCodeLookup.js';

export interface FormValues {
  street: string;
  postalCode: string;
  city: string;
  email: string;
  phone: string;
}

export type FieldName = keyof FormValues;

export const FIELD_ORDER: readonly FieldName[] = ['street', 'postalCode', 'city', 'email', 'phone'];

/** Static local-domain suggestion set — the proto's fixed list, no network. */
const EMAIL_DOMAINS: readonly string[] = ['gmail.com', 'outlook.com', 'hotmail.com'];

/** Per-field local validation; returns the i18n error key, or null when valid. */
export function validateField(field: FieldName, value: string): MessageKeys | null {
  const trimmed = value.trim();
  if (field === 'postalCode') {
    return /^\d{5}$/.test(trimmed) ? null : 'profile.verify.error.postalCode';
  }
  if (field === 'email') {
    // Shared with DemoMemberProfileAdapter.isValidEmail (member-profile-port-extension
    // contract point 5 lists malformed email as a rejection case): require a local
    // part, an @, and a dotted domain. Deliberately not a TLD allowlist.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? null : 'profile.verify.error.email';
  }
  const NON_EMPTY_ERROR = {
    street: 'profile.verify.error.street',
    city: 'profile.verify.error.city',
    phone: 'profile.verify.error.phone',
  } as const;
  return trimmed.length > 0 ? null : NON_EMPTY_ERROR[field];
}

/**
 * Derive email-domain suggestions for a partially-typed address (proto rules):
 *   - one `@`, non-empty local part, domain typed < 3 chars → all domains.
 *   - domain typed ≥ 3 chars → domains whose name starts with what's typed.
 *   - otherwise → no suggestions.
 * Pure: a function of the typed value only, so the caller can compute it inline.
 */
export function deriveEmailSuggestions(value: string): readonly string[] {
  const atCount = value.length - value.replaceAll('@', '').length;
  if (atCount !== 1) {
    return [];
  }
  const [localPart = '', domainPart = ''] = value.split('@');
  if (localPart.length === 0) {
    return [];
  }
  if (domainPart.length < 3) {
    return EMAIL_DOMAINS.map((domain) => `${localPart}@${domain}`);
  }
  return EMAIL_DOMAINS.filter((domain) =>
    domain.toLowerCase().startsWith(domainPart.toLowerCase()),
  ).map((domain) => `${localPart}@${domain}`);
}

export interface ContactEditForm {
  readonly values: FormValues;
  readonly errors: Partial<Record<FieldName, MessageKeys>>;
  /** True while the async write is in flight (Save disabled; no double-submit). */
  readonly isSubmitting: boolean;
  /**
   * Form-level failure after a write attempt (035 US1): the honest localized
   * message key for a validation / conflict / unavailable outcome. `null` when no
   * write has failed. Never a raw error (FR-007/FR-008).
   */
  readonly submitError: MessageKeys | null;
  readonly emailSuggestions: readonly string[];
  readonly fieldIdPrefix: string;
  readonly setFieldValue: (field: FieldName, value: string) => void;
  readonly selectEmailSuggestion: (suggestion: string) => void;
  readonly dismissEmailSuggestions: () => void;
  readonly submit: () => void;
  /**
   * Abandon the current edit session: reseed the values from the live read and drop
   * every transient state (per-field errors, the form-level failure, suggestions, and
   * the held idempotency key). Call it when leaving OR re-entering edit mode —
   * without it a failed save leaves a dead error message rendered for the member's
   * next attempt, which reads as a fresh failure that never happened (P7).
   */
  readonly reset: () => void;
}

/** Options for `useContactEditForm`. `onSaved` fires after a successful write. */
export interface ContactEditFormOptions {
  /** Called once the contact write persists (the takeover pairs this with
   *  `recordVerification('updated')` + dismiss). */
  readonly onSaved?: () => void;
}

/**
 * True when the two field-value sets are identical (idempotency-key stability).
 * Compares on TRIMMED values: the payload sent to the back office is trimmed, so
 * `"Helsinki"` and `"Helsinki "` are the same save and must not mint a fresh key
 * (which would defeat FR-006 dedup on a whitespace-only retry).
 */
function sameValues(a: FormValues, b: FormValues): boolean {
  return FIELD_ORDER.every((f) => a[f].trim() === b[f].trim());
}

export function useContactEditForm(options: ContactEditFormOptions = {}): ContactEditForm {
  const profile = useMemberProfile();
  const writer = useMemberProfileWriter();
  const lookupCity = usePostalCodeLookup();
  const fieldIdPrefix = useId();

  const [values, setValues] = useState<FormValues>({
    // The live IS contact block carries nullable optional fields; the editable
    // rows are plain strings, so an absent value seeds empty (never fabricated).
    street: profile.contact.streetAddress ?? '',
    postalCode: profile.contact.postalCode ?? '',
    city: profile.contact.city ?? '',
    email: profile.contact.email,
    phone: profile.contact.phone ?? '',
  });
  // Per-field error i18n keys (empty until a submit attempt surfaces them).
  const [errors, setErrors] = useState<Partial<Record<FieldName, MessageKeys>>>({});
  // Async write state (035 US1): in-flight indicator + honest form-level failure.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<MessageKeys | null>(null);
  // In-flight guard. Deliberately a REF, not the `isSubmitting` state: two taps
  // inside one React batch both read the same render's `isSubmitting === false`,
  // so a state-only guard lets both writes go out (T011 pins this). The shared
  // idempotency key means the back office would still apply the change once, but
  // FR-006 requires the member not be ABLE to double-submit — not merely that the
  // duplicate be absorbed upstream.
  const submittingRef = useRef(false);
  // The one idempotency-key lifecycle (FR-006): frozen across retries of the same
  // five field values, rebound when they change, cleared on success.
  const idempotencyKey = useIdempotencyKey<FormValues>(sameValues);
  // Email-domain suggestions for the currently-typed address (proto autocomplete).
  const [emailSuggestions, setEmailSuggestions] = useState<readonly string[]>([]);

  // Plain handlers — the React Compiler memoizes them; none are used as an
  // effect dependency, so a manual useCallback would be the anti-pattern the
  // react-compiler-uniformity check forbids.
  const setCityFromPostal = (postalCode: string): void => {
    if (!/^\d{5}$/.test(postalCode)) {
      return;
    }
    // The port may answer sync (demo) or async (future adapter). A non-null
    // city overwrites the field; a null leaves the member's typed value.
    void Promise.resolve(lookupCity(postalCode)).then((result) => {
      if (result.city !== null) {
        const resolvedCity = result.city;
        setValues((prev) => ({ ...prev, city: resolvedCity }));
      }
    });
  };

  const setFieldValue = (field: FieldName, value: string): void => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (field === 'postalCode') {
      setCityFromPostal(value.trim());
    }
    if (field === 'email') {
      setEmailSuggestions(deriveEmailSuggestions(value));
    }
  };

  const selectEmailSuggestion = (suggestion: string): void => {
    setValues((prev) => ({ ...prev, email: suggestion }));
    setEmailSuggestions([]);
  };

  const dismissEmailSuggestions = (): void => {
    setEmailSuggestions([]);
  };

  const reset = (): void => {
    // Reseed from the LIVE read, never from a remembered draft — the profile may have
    // been re-read since this session started (route-on-read, FR-009).
    setValues({
      street: profile.contact.streetAddress ?? '',
      postalCode: profile.contact.postalCode ?? '',
      city: profile.contact.city ?? '',
      email: profile.contact.email,
      phone: profile.contact.phone ?? '',
    });
    setErrors({});
    setSubmitError(null);
    setEmailSuggestions([]);
    idempotencyKey.clear();
  };

  const submit = (): void => {
    if (submittingRef.current) {
      return; // in-flight guard — no double-submit
    }
    setSubmitError(null);

    // Local validation first — collect every field error so the member sees all
    // problems at once rather than one-at-a-time.
    const nextErrors: Partial<Record<FieldName, MessageKeys>> = {};
    for (const field of FIELD_ORDER) {
      const errorKey = validateField(field, values[field]);
      if (errorKey !== null) {
        nextErrors[field] = errorKey;
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      // Move focus to the first invalid field so its aria-describedby error is
      // announced to assistive tech; a failed submit otherwise leaves focus on
      // the submit button with no spoken feedback (also helps sighted keyboard users).
      const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field] !== undefined);
      if (firstInvalid !== undefined) {
        document.getElementById(`${fieldIdPrefix}-${firstInvalid}`)?.focus();
      }
      return;
    }

    // Build the write patch: the 5 proto-edited fields + the 3 carried UNCHANGED
    // from the current read (preferredName / additionalAddress / countryId — not
    // editable here, never cleared or fabricated; FR-004, clarified 2026-08-07).
    const patch: ContactDetailsPatch = {
      email: values.email.trim(),
      phone: values.phone.trim(),
      streetAddress: values.street.trim(),
      postalCode: values.postalCode.trim(),
      city: values.city.trim(),
      preferredName: profile.contact.preferredName,
      additionalAddress: profile.contact.additionalAddress,
      countryId: profile.contact.countryId,
    };

    // Idempotency key: reused when the payload is unchanged (a retry → one applied
    // write), fresh when it changed (a new intent). See useIdempotencyKey.
    const key = idempotencyKey.keyFor(values);

    submittingRef.current = true;
    setIsSubmitting(true);
    void writer.updateContact(patch, key).then((outcome) => {
      submittingRef.current = false;
      setIsSubmitting(false);
      if (outcome.status === 'ok') {
        idempotencyKey.clear(); // the save-intent is done; the next Save mints afresh
        options.onSaved?.();
        return;
      }
      // Honest, localized, form-level failure — never a raw error (FR-007/FR-008).
      setSubmitError(
        outcome.status === 'conflict'
          ? 'profile.verify.saveError.conflict'
          : outcome.status === 'validation'
            ? 'profile.verify.saveError.validation'
            : outcome.status === 'not-found'
              ? 'profile.verify.saveError.notFound'
              : 'profile.verify.saveError.unavailable',
      );
    });
  };

  return {
    values,
    errors,
    isSubmitting,
    submitError,
    emailSuggestions,
    fieldIdPrefix,
    setFieldValue,
    selectEmailSuggestion,
    dismissEmailSuggestions,
    submit,
    reset,
  };
}
